/**
 * Auth utility functions for RC Studios
 * Handles Supabase authentication and token management
 */
import { supabase } from './supabase';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Storage keys for impersonation (separate from Supabase session)
const IMPERSONATION_TOKEN_KEY = 'rc_impersonation_access_token';

/**
 * Get the current access token (Supabase or impersonation)
 */
export async function getAccessToken() {
  // Check for impersonation token first
  const impersonationToken = localStorage.getItem(IMPERSONATION_TOKEN_KEY);
  if (impersonationToken) {
    return impersonationToken;
  }
  
  // Get Supabase session token
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
}

/**
 * Check if currently impersonating
 */
export function isImpersonating() {
  return !!localStorage.getItem(IMPERSONATION_TOKEN_KEY);
}

/**
 * Set impersonation token
 */
export function setImpersonationToken(token) {
  localStorage.setItem(IMPERSONATION_TOKEN_KEY, token);
}

/**
 * Clear impersonation token
 */
export function clearImpersonationToken() {
  localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
}

/**
 * Clear all auth (logout)
 */
export async function clearAuth() {
  clearImpersonationToken();
  await supabase.auth.signOut();
}

/**
 * Check if user has a valid session
 */
export async function hasValidSession() {
  const { data: { session } } = await supabase.auth.getSession();
  return !!session;
}

/**
 * Authenticated fetch wrapper
 * - Attaches Authorization header with Supabase token (or impersonation token)
 * - On 401, clears session and redirects to login
 */
export async function authFetch(url, options = {}, redirectOnFail = true) {
  const token = await getAccessToken();
  
  // Prepare headers
  const headers = {
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let response = await fetch(url, {
    ...options,
    headers,
  });

  // If unauthorized (and not impersonating), clear session
  if (response.status === 401 && !isImpersonating()) {
    await clearAuth();
    if (redirectOnFail && typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }

  return response;
}

/**
 * API helper for auth endpoints (Supabase-based)
 */
export const authAPI = {
  /**
   * Login with email and password via Supabase
   */
  async login(email, password) {
    const normalizedEmail = email.toLowerCase().trim();
    const { data, error } = await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password,
    });
    
    if (error) {
      throw new Error(error.message || 'Invalid email or password');
    }
    
    return data;
  },

  /**
   * Register a new user via Supabase
   * @param {string} email 
   * @param {string} password 
   * @param {string} returnPath - Optional path to return to after email confirmation
   */
  async register(email, password, returnPath = '/') {
    const normalizedEmail = email.toLowerCase().trim();
    
    // Build the redirect URL - go to /email-confirmed with the return path encoded
    // This way we handle the confirmation flow properly
    const emailConfirmedUrl = returnPath && returnPath !== '/' 
      ? `${window.location.origin}/email-confirmed?returnTo=${encodeURIComponent(returnPath)}`
      : `${window.location.origin}/email-confirmed`;
    
    const { data, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        emailRedirectTo: emailConfirmedUrl,
      },
    });
    
    if (error) {
      throw new Error(error.message || 'Registration failed');
    }
    // Supabase returns a user with no identities when the email is already registered
    if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      throw new Error('An account with this email already exists. Please sign in instead.');
    }
    
    return {
      ...data,
      message: 'Check your email to confirm your account.',
    };
  },

  /**
   * Request password reset via Supabase
   * Returns the actual error if one occurs (for debugging)
   */
  async forgotPassword(email) {
    const normalizedEmail = email.toLowerCase().trim();
    console.log('[Auth] Requesting password reset for:', normalizedEmail);
    
    const { data, error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    
    if (error) {
      // Log the actual error for debugging
      console.error('[Auth] Password reset error:', error.message, error);
      
      // Return the error info but with a generic user message
      // This prevents email enumeration while still logging real errors
      return { 
        status: 'success',  // Always show success to user
        message: 'If your email is registered, you will receive a password reset link.',
        _debug: { error: error.message, code: error.status }  // For console debugging
      };
    }
    
    console.log('[Auth] Password reset email sent successfully');
    return { 
      status: 'success', 
      message: 'If your email is registered, you will receive a password reset link.' 
    };
  },

  /**
   * Update password using Supabase (for reset password page)
   * The recovery link establishes a session, then we call updateUser
   */
  async resetPassword(newPassword) {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    
    if (error) {
      throw new Error(error.message || 'Password reset failed');
    }
    
    return { 
      status: 'success', 
      message: 'Password has been reset. You can now sign in with your new password.' 
    };
  },

  /**
   * Get current user from backend (creates MongoDB profile if needed)
   */
  async getMe() {
    const response = await authFetch(`${API_BASE}/api/auth/me`, {}, false);
    if (!response.ok) {
      throw new Error('Failed to fetch user');
    }
    return response.json();
  },

  /**
   * Logout - sign out of Supabase and clear any impersonation
   */
  async logout() {
    await clearAuth();
  },

  /**
   * Change password for authenticated user via Supabase
   */
  async changePassword(currentPassword, newPassword) {
    // Supabase doesn't require current password for authenticated users
    // But we can verify it first by re-authenticating
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.email) {
      throw new Error('No active session');
    }
    
    // Re-verify current password by signing in
    const { error: verifyError } = await supabase.auth.signInWithPassword({
      email: session.user.email,
      password: currentPassword,
    });
    
    if (verifyError) {
      throw new Error('Current password is incorrect');
    }
    
    // Now update to new password
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    
    if (error) {
      throw new Error(error.message || 'Password change failed');
    }
    
    return { status: 'success', message: 'Password changed successfully.' };
  },

  /**
   * Change email via Supabase
   */
  async changeEmail(newEmail, password) {
    const normalizedEmail = newEmail.toLowerCase().trim();
    
    // Verify password first
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.email) {
      throw new Error('No active session');
    }
    
    const { error: verifyError } = await supabase.auth.signInWithPassword({
      email: session.user.email,
      password,
    });
    
    if (verifyError) {
      throw new Error('Invalid password');
    }
    
    // Request email change
    const { error } = await supabase.auth.updateUser({
      email: normalizedEmail,
    });
    
    if (error) {
      throw new Error(error.message || 'Email change request failed');
    }
    
    return { 
      status: 'success', 
      message: 'Confirmation email sent to your new email address.' 
    };
  },
};

export default authAPI;
